import { FC, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, CircleAlert, ArrowRight } from 'lucide-react';
import { Can } from '@new/casl';
import { AddCourseModal } from '../AddCourseModal/AddCourseModal';
import { CourseWithProgress, useGetCurrentUserQuery } from '@teachur/http-client';
import { getActiveCoursesMessage } from './AllCoursesHeader.utils';
import { Button } from '@new/common/ui';

interface AllCoursesHeaderProps {
  courses: CourseWithProgress[];
}

export const AllCoursesHeader: FC<AllCoursesHeaderProps> = ({ courses }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: user } = useGetCurrentUserQuery();
  const activeCourseCount = courses.filter(
    (uc) => uc.status === 'inProgress' && !uc.isPaused
  ).length;
  const pausedCourseCount = courses.filter((uc) => uc.isPaused).length;

  const onOpenModal = () => setIsAddModalOpen(true);
  const onCloseModal = () => setIsAddModalOpen(false);
  const onGoToCourseBuilder = () => navigate({ to: '/course-builder' });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center gap-8">
        <h1 className="[font-size:var(--fs-h5)]! lg:[font-size:var(--fs-h2)]! [font-family:var(--font-montserrat)]! font-bold! text-(--neutral-black-NB1000)! m-0!">
          Courses
        </h1>
        <Can I="enroll" a="Degree">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="bg-(--primary-O100)"
              onClick={onOpenModal}
              leftIcon={
                <Plus size={16} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
              }
            >
              <span className="hidden lg:inline">Add course</span>
            </Button>
            <Button
              variant="primary"
              text="Your degree plan"
              rightIcon={<ArrowRight className="size-4" />}
              onClick={onGoToCourseBuilder}
            />
          </div>
        </Can>
      </div>

      <Can I="enroll" a="Degree">
        <div className="flex w-full min-h-13 p-3 items-center gap-3 rounded-lg bg-(--warning-W200) border border-(--warning-W400)">
          <CircleAlert className="text-(--warning-W700) shrink-0" size={20} />
          <span className="text-(--warning-W700) [font-family:var(--font-montserrat)]! [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p2)]! font-semibold leading-[150%]">
            {getActiveCoursesMessage(activeCourseCount, pausedCourseCount)}
          </span>
        </div>
      </Can>

      <AddCourseModal isOpen={isAddModalOpen} onClose={onCloseModal} userId={user.id} />
    </div>
  );
};
